import React, {Component, ReactElement, ReactNode} from 'react'
import {AsyncCommunicationProps} from '../../../../components/shared/hoc/withAsyncCommunication'
import {AppAdminContext} from '../../../../app/AppAdminContext'
import EventEmitterExAdmin from '../../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {Nullable} from '../../../../app/TypeHelpers'
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import {AdminPageHeadingArea} from '../../../../components/shared/pageBaseComponents/AdminPageHeadingArea'
import ListSearchFormArea from '../../../../styledComponents/adminPage/ListSearchFormArea'
import {
    ConditionButtonCell,
    ConditionCategoryCell,
    ConditionInputCellForColgroup,
    ConditionTableBody,
    ConditionTableFixed,
    ConditionTableRow,
} from '../../../../styledComponents/adminPage/ConditionTable'
import PPTextField from '../../../../components/shared/forms/PPTextField'
import {AdminDefaultMediumButton, AdminPrimaryButton} from '../../../../styledComponents/adminPage/Buttons'
import {Messages} from '../../../../Messages'
import {PageFooterArea, PageFotterAreaLeft, PageMainArea} from '../../../../components/shared/hoc/withAdminPageTemplate'
import {BackLink} from '../../../../styledComponents/adminPage/Links'
import styled from 'styled-components'
import PPSelect, {OptionProps} from '../../../../components/shared/forms/PPSelect'
import {NatGetRidge, RidgeData} from '../../ridge/repository/NatGetRidge'
import {
    NatGetAdminRoomNumberList,
    RoomNumberInfo,
    SearchCondition,
} from '../../roomNumber/repository/NatGetAdminRoomNumberList'
import NatRoomNumberRepo from '../../roomNumber/repository/NatRoomNumberRepo'
import {convertToRoomNumberForResident} from '../../../common/util/Natutil'
import NatRidgeRepo from '../../ridge/repository/NatRidgeRepo'
import {SelectChangeEvent} from '@mui/material/Select'
import {Location, NavigateFunction} from 'react-router-dom'

interface Props {
    eventEmitter: EventEmitterExAdmin
    ridgeRepo: NatRidgeRepo
    handleCloseRoomNumberSelectionDialog: () => void
    handleOpenCondominiumSelectionDialog: () => void
    navigate: NavigateFunction
    location: Location
    selectedTargetCondominiumInfo: RoomNumberInfo
    roomNumberRepo: NatRoomNumberRepo
    setSelectedTargetRoomNumberInfo: (SelectedTargetRoomNumberInfo: RoomNumberInfo) => void
}

interface States {
    isLoadCompleted: boolean
    ridge: RidgeData[] | null
    searchCondition: SearchCondition
    clickSearchButton: boolean
    roomNumberList: RoomNumberInfo[]
}

export default class NatTargetRoomNumberSelectionComponent extends Component<Props & AsyncCommunicationProps, States> {

    constructor(props: Props & AsyncCommunicationProps) {
        super(props)

        this.state = {
            isLoadCompleted: false,
            ridge: null,
            searchCondition: {
                condominiumCd: '',
                ridgeCd: '',
                roomNumber: '',
            },
            clickSearchButton: false,
            roomNumberList: [],
        }

        this.onChangeRidgeCd = this.onChangeRidgeCd.bind(this)
        this.onClickSearchButton = this.onClickSearchButton.bind(this)
        this.onChangeRoomNumber = this.onChangeRoomNumber.bind(this)
        this.onClickBackSelectCondominiumDiaLogLink = this.onClickBackSelectCondominiumDiaLogLink.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const ridgeResponse: Nullable<NatGetRidge> = await this.props.asyncCallFunction(
                this.props.ridgeRepo.getRidgeList.bind(
                    this.props.ridgeRepo,
                    this.props.selectedTargetCondominiumInfo.condominiumCd ?
                        this.props.selectedTargetCondominiumInfo.condominiumCd : undefined),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (ridgeResponse && ridgeResponse.details) {
                this.setState({
                    ridge: ridgeResponse.details,
                })
            }

        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    onChangeRidgeCd(event: SelectChangeEvent<unknown>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.ridgeCd = event.target.value as string
        this.setState({searchCondition: searchCondition})
    }

    onChangeRoomNumber(event: React.ChangeEvent<HTMLInputElement>): void {
        const seachConditions = this.state.searchCondition
        seachConditions.roomNumber = event.target.value
        this.setState({searchCondition: seachConditions})
    }

    getRidgeList(): OptionProps[] {
        let ridges: OptionProps[] = []
        if (this.state.ridge) {
            ridges = this.state.ridge.map((ridge) => {
                return ({
                    label: `${ridge.ridgeCd} : ${ridge.ridgeName}`,
                    value: `${ridge.ridgeCd}`,
                })
            })
        }

        return [{
            label: 'すべて表示',
            value: '',
        }, ...ridges]
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    async onClickSearchButton(): Promise<void> {
        this.beforeUserAction()

        const searchCondition = this.state.searchCondition
        await this.getRoomNumberList(
            searchCondition
        )
        this.setState({searchCondition: searchCondition})
    }

    async getRoomNumberList(
        searchCondition: SearchCondition,
    ): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            let roomNumberResponse: Nullable<NatGetAdminRoomNumberList>
            roomNumberResponse = await this.props.asyncCallFunction(
                this.props.roomNumberRepo.getAdminRoomNumberList.bind(
                    this.props.roomNumberRepo,
                    this.props.selectedTargetCondominiumInfo.condominiumCd,
                    searchCondition.ridgeCd,
                    searchCondition.roomNumber,
                ),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )

            if (roomNumberResponse?.details) {
                this.setState({
                    roomNumberList: roomNumberResponse.details.roomNumberInfo,
                    clickSearchButton: true,
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    onClickSelectButton(roomNumber: RoomNumberInfo): void {
        if (this.props.setSelectedTargetRoomNumberInfo) {
            this.props.setSelectedTargetRoomNumberInfo(roomNumber)
            this.props.handleCloseRoomNumberSelectionDialog()
        }
    }

    onClickBackSelectCondominiumDiaLogLink(): void {
        this.props.handleCloseRoomNumberSelectionDialog()
        this.props.handleOpenCondominiumSelectionDialog()
    }

    renderRoomNumberTable(roomNumberList: RoomNumberInfo[]): Nullable<ReactElement> {
        if (roomNumberList.length === 0) {
            return null
        }

        return (
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell key={'condominiumName'} style={{width: '400px'}} align={'left'}>物件名</TableCell>
                        <TableCell key={'condominiumCd'} style={{width: '600px'}} align={'left'}>部屋番号</TableCell>
                        <TableCell key={'operation'} style={{width: '200px'}} align={'left'}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {this.state.roomNumberList.map(
                        roomNumber => this.renderRoomNumberRecord(roomNumber),
                    )}
                </TableBody>
            </Table>
        )
    }

    renderRoomNumberRecord(roomNumber: RoomNumberInfo): ReactNode {
        return (
            <TableRow key={roomNumber.condominiumCd}>
                <TableCell style={{width: '400px'}} align={'left'}>{roomNumber.condominiumName}</TableCell>
                <TableCell style={{width: '600px'}} align={'left'}>
                    {`${roomNumber.ridgeName} ${convertToRoomNumberForResident(roomNumber.roomNumber)}`}
                </TableCell>
                <TableCell style={{width: '200px'}} align={'center'}>
                    <AdminPrimaryButton onClick={() => this.onClickSelectButton(roomNumber)}>
                        選択
                    </AdminPrimaryButton>
                </TableCell>
            </TableRow>
        )
    }


    renderDialog(): ReactNode {
        return <>
            <Dialog
                scroll={'paper'}
                id={'pp-dialog'}
                open={true}
                transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                maxWidth={'xl'}
            >
                <StyledDialogFixed>
                    <AdminPageHeadingArea
                        title={'対象物件・部屋番号選択（2/2）'}
                        onClose={this.props.handleCloseRoomNumberSelectionDialog}
                    />
                </StyledDialogFixed>
                <StyledDialogContent>
                    <ListSearchFormArea
                        onEnterKey={this.onClickSearchButton}
                    >
                        <ConditionTableFixed>
                            <colgroup>
                                <col width={'10%'}/>
                                <col width={'10%'}/>
                                <col width={'20%'}/>
                                <col width={'10%'}/>
                                <col width={'20%'}/>
                            </colgroup>
                            <ConditionTableBody>
                                <ConditionTableRow>
                                    <ConditionCategoryCell rowSpan={2}>物件情報</ConditionCategoryCell>
                                    <ConditionInputCellForColgroup>物件名</ConditionInputCellForColgroup>
                                    <ConditionInputCellForColgroup>
                                        {this.props.selectedTargetCondominiumInfo.condominiumName}
                                    </ConditionInputCellForColgroup>
                                </ConditionTableRow>
                                <ConditionTableRow>
                                    <ConditionInputCellForColgroup>棟名</ConditionInputCellForColgroup>
                                    <ConditionInputCellForColgroup>
                                        <PPSelect
                                            data={this.getRidgeList()}
                                            onChange={this.onChangeRidgeCd.bind(this)}
                                            value={this.state.searchCondition.ridgeCd}
                                            eventEmitter={this.props.eventEmitter}
                                            fullWidth
                                        />
                                    </ConditionInputCellForColgroup>
                                    <ConditionInputCellForColgroup>部屋番号</ConditionInputCellForColgroup>
                                    <ConditionInputCellForColgroup>
                                        <PPTextField
                                            InputProps={{
                                                inputProps: {
                                                    maxLength: 4,
                                                },
                                            }}
                                            fullWidth
                                            onChange={this.onChangeRoomNumber}
                                            defaultValue={this.state.searchCondition.roomNumber}
                                            autoFocus
                                        />
                                    </ConditionInputCellForColgroup>
                                    <ConditionButtonCell>
                                        <AdminDefaultMediumButton onClick={this.onClickSearchButton}>
                                            部屋番号を検索する
                                        </AdminDefaultMediumButton>
                                    </ConditionButtonCell>
                                </ConditionTableRow>
                            </ConditionTableBody>
                        </ConditionTableFixed>
                    </ListSearchFormArea>
                    <SearchResultTotal>
                        {this.state.clickSearchButton &&
                            <Box mt={'5px'} ml={'10px'}>
                                <Typography variant={'body2'}>
                                    {this.state.roomNumberList.length > 0 &&
                                        this.state.roomNumberList.length + '件'
                                    }
                                    {this.state.roomNumberList.length === 0 &&
                                        '0件 ' + Messages.IOPCC0014.toString('')
                                    }
                                </Typography>
                            </Box>
                        }
                    </SearchResultTotal>
                    <PageMainArea>
                        {this.renderRoomNumberTable(this.state.roomNumberList)}
                    </PageMainArea>
                </StyledDialogContent>
                <StyledDialogActions>
                    <PageFooterArea>
                        <PageFotterAreaLeft>
                            <BackLink onClick={this.onClickBackSelectCondominiumDiaLogLink}>
                                対象物件の選択へ戻る
                            </BackLink>
                        </PageFotterAreaLeft>
                    </PageFooterArea>
                </StyledDialogActions>
            </Dialog>
        </>
    }

    render(): Nullable<ReactNode> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        return this.renderDialog()
    }
}
NatTargetRoomNumberSelectionComponent.contextType = AppAdminContext

const StyledDialogFixed = styled(DialogTitle)`
  &.MuiDialogTitle-root {
    padding: 24px 24px 0;
    min-width: 960px;
  }
`

const StyledDialogContent = styled(DialogContent)`
  &.MuiDialogContent-root {
    padding: 0 24px 8px;
    min-width: 960px;
  }
`

const StyledDialogActions = styled(DialogActions)`
  &.MuiDialogActions-root {
    padding: 8px 24px 24px;
  }
`

const SearchResultTotal = styled.div`
  padding: 8px 12px;
`
